import {
  ColorIcon,
  colors,
  customEvent,
  getCdnImageSrc,
  HLine,
  HStack,
  icon,
  useAlertDialog,
  VLine,
  VStack,
} from "goi_common";
import { useRouter } from "next/router";
import { Circle } from "@chakra-ui/react";

import { getObituarySendMessageTemplate } from "@/hooks/obituary/useObituaryNativeShare";
import { useGetObituary } from "@/containers/BugoContainer/BugoCreateContainer/hooks/useGetObituary";
import { getIsFlowerOrderEnabled } from "@/containers/BugoContainer/utils";
import { BUGO_CHOICE_SHARE_TYPE } from "pages/bugo/share/choice";

interface BugoShareChoiceModalContentProps {
  type: "visitor" | "sangju" | "writer";
  onClose: () => void;
  obituaryKey?: string;
}

/** writer를 props가 아니라 내부적으로 판단해야할 것 같음  */
export const BugoShareChoiceModalContent = ({
  type,
  onClose,
  obituaryKey,
}: BugoShareChoiceModalContentProps) => {
  const router = useRouter();
  const { openAlertDialog } = useAlertDialog();

  const { data: obituary, isLoading } = useGetObituary(obituaryKey);

  const is부고작성자 = obituaryKey ? true : false;
  const sangjuKey = obituary?.sangjuInfo?.key;

  const 부고정보 = {
    고인이름: obituary?.name,
    관계: obituary?.sangjuInfo?.relation,
    상주이름: obituary?.sangjuInfo?.name,
    날짜: obituary?.passed_away_on || "",
    발인: obituary?.leave_parlor_at || "",
    장례식장: obituary?.parlor?.companyname || "",
    빈소: obituary?.binso,
    사망표현: obituary?.die_term,
  };

  const onClickShareMessage = () => {
    customEvent({
      path: "bugo_share",
      trigger_and_target: "btn_message-share",
    });

    return is부고작성자
      ? router.push(
          `/bugo/share/choice?obituaryKey=${obituaryKey}&shareType=${BUGO_CHOICE_SHARE_TYPE.message}`
        )
      : shareMessageTemplate();
  };

  const shareMessageTemplate = () => {
    const shareURL = `${window.location.origin}/share/visitor/${sangjuKey}/`;

    const message = encodeURIComponent(
      getObituarySendMessageTemplate(부고정보)
    );

    // /** android */
    // ("sms:?body=/* body text here */");
    // /** ios8 미만 */
    // ("sms:body=/* body text here */");
    // /** ios8 이상 */
    // ("sms:&body=/* body text here */");
    router.push(`sms:?body=${shareURL + "\n" + message}`);
  };

  const shareKakaoTextTemplate = () => {
    customEvent({
      path: "bugo_share",
      trigger_and_target: "btn_kakao-share",
    });

    /** sangju, 조문객 공유 */
    if (!window?.Kakao) return;

    const isFlowerOrderEnabled = getIsFlowerOrderEnabled(obituary);

    if (type === "visitor") {
      // window.location.href에 이미 ?temporary_key=... 같은 쿼리가 있는 경우
      // 단순 `?share=kakao` 연결은 `?`가 2번 붙어 URL이 깨진다.
      // URL/URLSearchParams로 안전하게 set.
      const visitorUrlObj = new URL(window.location.href);
      visitorUrlObj.searchParams.set("share", "kakao");
      const visitorUrl = visitorUrlObj.toString();
      const flowerUrl = `${window.location.origin}/flower/select?sangjuKey=${sangjuKey}&share=kakao`;

      const buttons: {
        title: string;
        link: { webUrl: string; mobileWebUrl: string };
      }[] = [
        {
          title: "부고장 확인하기",
          link: { webUrl: visitorUrl, mobileWebUrl: visitorUrl },
        },
      ];
      if (isFlowerOrderEnabled) {
        buttons.push({
          title: "근조화환 보내기",
          link: { webUrl: flowerUrl, mobileWebUrl: flowerUrl },
        });
      }

      const shareContent = {
        objectType: "feed",
        content: {
          title: "부고 알림",
          description: getObituarySendMessageTemplate(부고정보),
          imageUrl: getCdnImageSrc("bugo/bugo_kakao_share.png"),
          imageWidth: 800,
          imageHeight: 400,
          link: {
            webUrl: visitorUrl,
            mobileWebUrl: visitorUrl,
          },
        },
        buttons,
      };

      return window.Kakao?.Share?.sendDefault(shareContent);
    }

    if (type === "sangju") {
      const sangjuVisitorUrl = `${window.location.origin}/share/visitor/${sangjuKey}?share=kakao`;
      const flowerUrl = `${window.location.origin}/flower/select?sangjuKey=${sangjuKey}&share=kakao`;

      const buttons: {
        title: string;
        link: { webUrl: string; mobileWebUrl: string };
      }[] = [
        {
          title: "부고장 확인하기",
          link: { webUrl: sangjuVisitorUrl, mobileWebUrl: sangjuVisitorUrl },
        },
      ];
      if (isFlowerOrderEnabled) {
        buttons.push({
          title: "근조화환 보내기",
          link: { webUrl: flowerUrl, mobileWebUrl: flowerUrl },
        });
      }

      const shareContent = {
        objectType: "feed",
        content: {
          title: "부고 알림",
          description: getObituarySendMessageTemplate(부고정보),
          imageUrl: getCdnImageSrc("bugo/bugo_kakao_share.png"),
          imageWidth: 800,
          imageHeight: 400,
          link: {
            webUrl: sangjuVisitorUrl,
            mobileWebUrl: sangjuVisitorUrl,
          },
        },
        buttons,
      };

      return window.Kakao?.Share?.sendDefault(shareContent);
    }

    return router.push(
      `/bugo/share/choice?obituaryKey=${obituaryKey}&shareType=${BUGO_CHOICE_SHARE_TYPE.kakao}`
    );
  };

  const copyShareLink = () => {
    customEvent({
      path: "bugo_share",
      trigger_and_target: "btn_copy-link",
    });

    if (type === "writer") {
      return router.push(
        `/bugo/share/choice?obituaryKey=${obituaryKey}&shareType=${BUGO_CHOICE_SHARE_TYPE.copyLink}`
      );
    } else {
      const copyClipboardShareTemplate = () => {
        const shareURL = `${window.location.origin}/share/visitor/${sangjuKey}/`;

        const message = getObituarySendMessageTemplate(부고정보);

        navigator.clipboard.writeText(shareURL + "\n\n" + message).then(() => {
          openAlertDialog({
            content: "복사되었습니다.",
            buttonText: "확인",
          });
        });
      };

      copyClipboardShareTemplate();
      return;
    }
  };

  if (type === "writer" && obituaryKey) {
    const onClickShareContact = () => {
      customEvent({
        path: "bugo_share",
        trigger_and_target: "btn_contact-share",
      });
      router.push(
        `/bugo/share/choice?obituaryKey=${obituaryKey}&shareType=${BUGO_CHOICE_SHARE_TYPE.contact}`
      );
    };

    const onClickInviteFamily = () => {
      customEvent({
        path: "bugo_share",
        trigger_and_target: "btn_invite_family-bugo_share",
      });
      router.push(`/bugo/share/invite-sangju?obituaryKey=${obituaryKey}`);
    };

    const 장례식장없음 = !obituary?.parlor?.companyname;
    const 빈소없음 = !obituary?.binso;
    const 상주없음 = !obituary?.sangjus || obituary?.sangjus?.length < 1;

    const 공유_못하는_상태 = 장례식장없음 || 빈소없음 || 상주없음;

    const 공유_못하는_상태_메시지 = `${상주없음 ? "'상주' " : ""} ${
      장례식장없음 ? "'장례식장' " : ""
    } ${빈소없음 ? "'빈소' " : ""}를 입력하지 않아 전달할 수 없어요`;

    const isShowRedirectInputButton = 공유_못하는_상태 && !isLoading;

    return (
      <VStack
        background={colors.white}
        css={{
          borderRadius: "20px",
          overflow: "hidden",
        }}
      >
        <VStack
          gap="2px"
          padding="28px 0px 32px 0px"
          justifyContent="center"
          css={{
            whiteSpace: "pre-wrap",
            textAlign: "center",
          }}
        >
          <div className="subtitle_21_sb gray800">부고 전달하기</div>
          <div className="gray500">주변 분들에게 부고를 전달하세요.</div>
        </VStack>
        <HLine color={colors.gray100} />

        <div
          css={{
            padding: "20px 21px",
          }}
        >
          <VStack
            css={{
              marginBottom: "24px",
            }}
          >
            <b className="subtitle_16_sb">가족에게 전달하기</b>
            <div className="caption_12_r gray600">
              가족들이 본인 계좌를 직접 입력할 수 있어요
            </div>
            <ShareChoiceModalItem
              iconName="icon-call-mono"
              iconBackgroundColor={colors.green350}
              title="가족에게 전달하기"
              disabled={false}
              onClick={onClickInviteFamily}
            />
          </VStack>

          <VStack>
            <b
              className="subtitle_16_sb"
              css={{
                opacity: 공유_못하는_상태 ? 0.5 : 1,
              }}
            >
              조문객에게 전달하기
            </b>
            {isShowRedirectInputButton && (
              <div className="caption_12_r danger">
                {공유_못하는_상태_메시지}
              </div>
            )}

            <VStack
              css={{
                position: "relative",
              }}
            >
              {isShowRedirectInputButton && (
                <button
                  className="button_14_sb white"
                  css={{
                    padding: "15px 20px",
                    position: "absolute",
                    left: "50%",
                    bottom: "50%",
                    transform: "translate(-50%, 50%)",
                    background: colors.warmGray850,
                    borderRadius: "5px",
                    zIndex: 10,
                  }}
                  onClick={() => {
                    customEvent({
                      path: "bugo_share",
                      trigger_and_target: "btn_input-bugo_update",
                    });
                    if (상주없음) {
                      router.push(
                        `/bugo/update/sangju?obituaryKey=${obituaryKey}`
                      );
                    } else {
                      router.push(
                        `/bugo/update/binso?obituaryKey=${obituaryKey}`
                      );
                    }
                  }}
                >
                  입력하러 가기
                </button>
              )}
              <VStack
                css={{
                  opacity: 공유_못하는_상태 ? 0.2 : 1,
                }}
              >
                <ShareChoiceModalItem
                  iconName={"icon-message"}
                  iconSize={"18px"}
                  iconBackgroundColor={colors.green350}
                  title={"문자메시지로 전달하기"}
                  disabled={공유_못하는_상태}
                  onClick={onClickShareMessage}
                />
                <VLine color={colors.gray100} width="100%" height="1px" />
                <ShareChoiceModalItem
                  iconName="icon-comment-mono"
                  iconBackgroundColor={"#fee500"}
                  title={"카카오톡으로 전달하기"}
                  disabled={공유_못하는_상태}
                  onClick={shareKakaoTextTemplate}
                />
                <VLine color={colors.gray100} width="100%" height="1px" />
                <ShareChoiceModalItem
                  iconName="icon-share"
                  iconBackgroundColor={colors.warmGray650}
                  title={"링크 복사하기"}
                  disabled={공유_못하는_상태}
                  onClick={copyShareLink}
                />
                <VLine color={colors.gray100} width="100%" height="1px" />
                <ShareChoiceModalItem
                  iconName={"icon-call-mono"}
                  iconBackgroundColor={colors.green350}
                  title="미리 등록한 연락처로 발송하기"
                  disabled={공유_못하는_상태}
                  onClick={onClickShareContact}
                />
              </VStack>
            </VStack>
          </VStack>
        </div>
      </VStack>
    );
  }

  return (
    <VStack
      background={colors.white}
      css={{
        borderRadius: "20px",
        overflow: "hidden",
      }}
    >
      <VStack
        gap="2px"
        padding="28px 0px 32px 0px"
        justifyContent="center"
        css={{
          whiteSpace: "pre-wrap",
          textAlign: "center",
        }}
      >
        <div className="subtitle_21_sb gray800">부고 전달하기</div>
        <div className="gray500">주변 분들에게 부고를 전달하세요.</div>
      </VStack>
      <HLine color={colors.gray100} />

      <div
        css={{
          padding: "20px 21px",
        }}
      >
        <VStack>
          <ShareChoiceModalItem
            iconName={"icon-message"}
            iconSize={"18px"}
            iconBackgroundColor={colors.green350}
            title={"문자메시지로 전달하기"}
            disabled={false}
            onClick={onClickShareMessage}
          />
          <VLine color={colors.gray100} width="100%" height="1px" />
          <ShareChoiceModalItem
            iconName="icon-comment-mono"
            iconBackgroundColor="#fee500"
            title="카카오톡으로 전달하기"
            disabled={false}
            onClick={shareKakaoTextTemplate}
          />
          <VLine color={colors.gray100} width="100%" height="1px" />
          <ShareChoiceModalItem
            iconName="icon-share"
            iconBackgroundColor={colors.warmGray650}
            title={"링크 복사하기"}
            disabled={false}
            onClick={copyShareLink}
          />
        </VStack>
      </div>
    </VStack>
  );
};

interface ShareChoiceModalItemProps {
  iconName: keyof typeof icon;
  iconSize?: `${number}px`;
  iconBackgroundColor: string;
  title: string;
  description?: string;
  showArrow?: boolean;
  disabled: boolean;
  onClick: () => void;
}

export const ShareChoiceModalItem = ({
  iconName,
  iconSize = "16px",
  iconBackgroundColor,
  title,
  description,
  showArrow = false,
  disabled,
  onClick,
}: ShareChoiceModalItemProps) => {
  return (
    <VStack padding="16px 0" width="100%">
      <button
        disabled={disabled}
        onClick={onClick}
        css={{
          height: "100%",
          cursor: disabled ? "default" : "pointer",
        }}
      >
        <HStack gap="12px" alignItems="center" height="100%">
          <Circle
            css={{
              width: "28px",
              height: "28px",
              background: iconBackgroundColor,
            }}
          >
            <ColorIcon name={iconName} color={""} size={iconSize} />
          </Circle>
          <VStack width="100%" justifyContent="center" alignItems="center">
            {title && (
              <div
                className="body_16_m"
                css={{
                  width: "100%",
                  textAlign: "left",
                }}
              >
                {title}
              </div>
            )}
            {description && (
              <div
                className="body_14_r gray600"
                css={{
                  textAlign: "left",
                }}
              >
                {description}
              </div>
            )}
          </VStack>
          {showArrow && (
            <HStack alignItems="center">
              <ColorIcon
                name={"icon-arrow-right-small-mono"}
                height="16px"
                color={""}
              />
            </HStack>
          )}
        </HStack>
      </button>
    </VStack>
  );
};
